/**
 * Background Jobs — Dashboard View.
 *
 * Lean layout: a date filter, the engine-health strip, live KPI cards, and the
 * "Live work" panel. Config cards (Jobs / Schedules / Next fire) read from
 * GET /dashboard so they are always populated; the run-outcome cards (Total runs,
 * Succeeded, Failed, Running, Success rate) count over the selected date window
 * from GET /runs. Run-outcome cards open a drill dialog → per-run detail drawer.
 *
 * Styled with the Error Log Dashboard's stylesheet so the two look the same.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'sharedUi/Dropdown';
import { useAccessibleProjects, projectOptions } from '../../../hooks/useProjectScope';
import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'sharedUi/Calendar';
import { backgroundJobService } from '../../../services/background-job.service';
import type { BackgroundJobDashboard, BackgroundJobRun, RunStatus, TriggeredBy } from '../../../types/background-job.types';
import { useJobPermissions } from './use-job-permissions';
import '../../error-analytics/error-analytics.css';
import './dashboard-view.css';
import RunsTab from './runs-tab';
import NowAndNext from './now-and-next';

/* ------------------------------------------------------------------ */
const RUN_C: Record<RunStatus, string> = {
  SUCCESS: '#22C55E', FAILED: '#EF4444', RETRYING: '#F59E0B', RUNNING: '#3B82F6', CANCELLED: '#94A3B8',
};
const statusLabel: Record<RunStatus, string> = {
  SUCCESS: 'Succeeded', FAILED: 'Failed', RETRYING: 'Retrying', RUNNING: 'Running', CANCELLED: 'Cancelled',
};
const trigLabel = (t?: TriggeredBy | string | null) =>
  t === 'MANUAL_USER' ? 'Manual' : t === 'SYSTEM_SCHEDULE' ? 'Schedule' : '—';

const fmt = (n: number) => Math.round(n || 0).toLocaleString('en-US');
function timeAgo(iso?: string | null): string {
  if (!iso) return '—'; const t = new Date(iso).getTime(); if (Number.isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000); if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`;
}
function fmtDur(a?: string | null, b?: string | null): string {
  if (!a || !b) return '—';
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000; if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60); return `${m}m ${Math.round(s % 60)}s`;
}
const startedOf = (r: BackgroundJobRun) => r.startedAt || r.createdAt || null;
function untilNextRun(iso?: string | null): string {
  if (!iso) return 'nothing scheduled';
  const ms = Date.parse(iso) - Date.now(); if (Number.isNaN(ms)) return 'nothing scheduled';
  if (ms <= 0) return 'due now';
  const mins = Math.round(ms / 60000); if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60); if (hours < 24) return `in ${hours}h ${mins % 60}m`;
  return new Date(iso).toLocaleString();
}

/** How far ahead the "Next fire" card looks. Nothing due inside it reads as "-". */
const NEXT_FIRE_WINDOW_MS = 2 * 60 * 1000;

/**
 * The "Next fire" card, which answers one question: is something about to run in
 * the next two minutes, and what is it?
 *
 * Outside that window the card deliberately shows "-" rather than a countdown —
 * a fire six hours out is not something an operator watching this screen can act
 * on, and showing it makes the card look live when it is not. A schedule already
 * due but not yet picked up (ms <= 0) still counts as imminent: that is exactly
 * the case worth seeing, since it means the poller may be behind.
 */
function nextFireCard(d?: BackgroundJobDashboard | null): { text: string; sub: string } {
  const iso = d?.nextSchedule?.nextRunAt ?? d?.nextRunAt ?? null;
  const ms = iso ? Date.parse(iso) - Date.now() : NaN;
  if (!iso || Number.isNaN(ms) || ms > NEXT_FIRE_WINDOW_MS) {
    return { text: '-', sub: 'nothing due in the next 2 min' };
  }
  const name = d?.nextSchedule?.scheduleName?.trim();
  // The countdown moves to the subtitle: the name is the answer now, the timing
  // is the detail behind it.
  return {
    text: name || 'unnamed schedule',
    sub: `${untilNextRun(iso)} · ${new Date(iso).toLocaleTimeString()}`,
  };
}

type TipContent = ReactNode;

function CountUp({ value, unit }: { value: number; unit?: string }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0; const t0 = performance.now(); const dur = 850;
    const step = (now: number) => { const p = Math.min((now - t0) / dur, 1); setV(value * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{Math.round(v).toLocaleString('en-US')}{unit ? <small>{unit}</small> : null}</>;
}
function StatusChip({ s }: { s: RunStatus }) {
  return <span className="ea-sev" style={{ background: `${RUN_C[s]}1f`, color: RUN_C[s] }}>{statusLabel[s]}</span>;
}

type DrillStatus = RunStatus | 'ALL';
interface KpiVM {
  key: string; label: string; color: string; tip: string;
  val?: number; unit?: string; text?: string; sub?: string; drill?: DrillStatus;
}

/* ================================================================== */
const DashboardView = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  /* Which project the whole dashboard is reporting on.
     Merged across every project the caller may read until one is picked — that
     is what these tiles have always shown, and a cross-project operator opening
     the page wants the whole picture first. Picking one narrows the tiles, the
     job list and the run cards together. */
  const [appFilter, setAppFilter] = useState('');
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [tip, setTip] = useState<{ show: boolean; x: number; y: number; content: TipContent }>({ show: false, x: 0, y: 0, content: null });
  const showTip = (content: TipContent, x: number, y: number) => setTip({ show: true, x, y, content });
  const hideTip = () => setTip((t) => ({ ...t, show: false }));

  const [view, setView] = useState<'overview' | 'records'>('overview');
  const [drill, setDrill] = useState<DrillStatus | null>(null);
  const [detail, setDetail] = useState<BackgroundJobRun | null>(null);
  const { canReadRuns } = useJobPermissions();

  const { fromDate, toDate, rangeLabel } = useMemo(() => {
    if (!startDate && !endDate) return { fromDate: undefined, toDate: undefined, rangeLabel: 'All time' };
    const start = startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : undefined;
    const end = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : undefined;
    const asLabel = (dd: Date) => dd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return {
      fromDate: start?.toISOString(), toDate: end?.toISOString(),
      rangeLabel: start && end ? (start.toDateString() === end.toDateString() ? asLabel(start) : `${asLabel(start)} → ${asLabel(end)}`) : start ? `From ${asLabel(start)}` : `Up to ${asLabel(end!)}`,
    };
  }, [startDate, endDate]);

  const applyPreset = (days: number | null) => {
    if (days === null) { setStartDate(null); setEndDate(null); }
    else { const end = new Date(); end.setHours(0, 0, 0, 0); const start = new Date(end); start.setDate(start.getDate() - (days - 1)); setStartDate(start); setEndDate(end); }
  };
  const activePreset = useMemo(() => {
    if (!startDate && !endDate) return 'all';
    if (!startDate || !endDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (endDate.toDateString() !== today.toDateString()) return null;
    const spanDays = Math.round((today.getTime() - new Date(startDate).setHours(0, 0, 0, 0)) / 864e5) + 1;
    return spanDays === 7 ? '7d' : spanDays === 30 ? '30d' : null;
  }, [startDate, endDate]);

  useEffect(() => {
    if (!dateOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDateOpen(false); };
    document.addEventListener('keydown', onKey); return () => document.removeEventListener('keydown', onKey);
  }, [dateOpen]);

  // ---- data ----
  const dashQ = useQuery({ queryKey: ['bg-dashboard', appFilter], queryFn: () => backgroundJobService.getDashboard(appFilter || undefined), staleTime: 15_000, refetchInterval: 30_000 });
  const hb = useQuery({ queryKey: ['bg-heartbeat'], queryFn: () => backgroundJobService.getHeartbeat(), staleTime: 5_000, refetchInterval: 15_000 });
  const jobsQ = useQuery({ queryKey: ['bg-dash-jobs', appFilter], queryFn: () => backgroundJobService.listJobs({ page_size: 500, include_deleted: true, appId: appFilter || undefined }), staleTime: 60_000 });
  // page_size is capped at 500 by the API; asking for more is a 422. Needs
  // BACKGROUND_JOB_RUN.READ — without it the run cards fall back to /dashboard.
  const runsQ = useQuery({
    queryKey: ['bg-dash-runs', fromDate, toDate, appFilter],
    // Narrowed with the tiles, not left behind: tiles for one project beside run
    // cards for every project is a screen that contradicts itself.
    queryFn: () => backgroundJobService.listRuns({ from: fromDate, to: toDate, page_size: 500, sort_by: 'startedAt', sort_order: 'desc', appId: appFilter || undefined }),
    enabled: canReadRuns,
    staleTime: 15_000, refetchInterval: 30_000,
  });

  const jobName = useMemo(() => {
    const m = new Map<string, string>();
    (jobsQ.data?.items ?? []).forEach((j) => m.set(j._id, j.jobName || j.jobKey));
    return (r: BackgroundJobRun) => r.jobName || m.get(r.jobId) || r.jobKey || r.jobId;
  }, [jobsQ.data]);

  const runs = useMemo(() => runsQ.data?.items ?? [], [runsQ.data]);
  const counts = useMemo(() => {
    const c: Record<RunStatus, number> = { SUCCESS: 0, FAILED: 0, RETRYING: 0, RUNNING: 0, CANCELLED: 0 };
    runs.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    const terminal = c.SUCCESS + c.FAILED;
    return { ...c, total: runs.length, rate: terminal ? Math.round((c.SUCCESS / terminal) * 100) : null };
  }, [runs]);

  const drillRuns = useMemo(() => drill == null ? [] : drill === 'ALL' ? runs : runs.filter((r) => r.status === drill), [drill, runs]);

  const d = dashQ.data;
  // When runs are readable, the outcome cards count over the picked window; when
  // they are not (no BACKGROUND_JOB_RUN.READ), they fall back to the server's
  // /dashboard figures so the cards are never blank.
  const total = canReadRuns ? counts.total : (d?.runsLast24h ?? 0);
  const succeeded = canReadRuns ? counts.SUCCESS : (d?.succeededToday ?? 0);
  const failed = canReadRuns ? counts.FAILED : (d?.failedToday ?? 0);
  const running = canReadRuns ? counts.RUNNING : (d?.runningJobs ?? 0);
  const rate = canReadRuns ? counts.rate : (d?.successRate24h ?? null);
  const rateColor = rate === null ? '#6366F1' : rate >= 95 ? '#22C55E' : rate >= 80 ? '#F59E0B' : '#EF4444';
  const win = rangeLabel === 'All time' ? 'all time' : rangeLabel.toLowerCase();
  const outcomeWin = canReadRuns ? win : 'last 24h';
  const drillFor = (s: DrillStatus): DrillStatus | undefined => (canReadRuns ? s : undefined);

  const nextFire = nextFireCard(d);

  const kpis: KpiVM[] = [
    { key: 'jobs', label: 'Jobs', color: '#6366F1', val: d?.totalJobs ?? 0, sub: d ? `${d.activeJobs} active · ${d.disabledJobs} inactive` : undefined, tip: 'Job definitions in your scope' },
    { key: 'schedules', label: 'Schedules', color: '#8B5CF6', val: d?.totalSchedules ?? 0, sub: d ? `${d.enabledSchedules} enabled` : undefined, tip: 'Schedules attached to jobs' },
    { key: 'next', label: 'Next fire', color: '#14B8A6', ...nextFire, tip: 'The schedule due to fire in the next 2 minutes, or "-" when nothing is' },
    { key: 'total', label: 'Total runs', color: '#0EA5E9', val: total, sub: outcomeWin, tip: `Runs recorded in ${outcomeWin}`, drill: drillFor('ALL') },
    { key: 'succeeded', label: 'Succeeded', color: RUN_C.SUCCESS, val: succeeded, sub: outcomeWin, tip: `Runs that finished successfully in ${outcomeWin}`, drill: drillFor('SUCCESS') },
    { key: 'failed', label: 'Failed', color: RUN_C.FAILED, val: failed, sub: outcomeWin, tip: `Runs that exhausted their retries in ${outcomeWin}`, drill: drillFor('FAILED') },
    { key: 'running', label: 'Running', color: RUN_C.RUNNING, val: running, sub: 'running, or awaiting sweep', tip: 'Runs not yet finalized', drill: drillFor('RUNNING') },
    { key: 'rate', label: 'Success rate', color: rateColor, text: rate === null ? 'no runs yet' : undefined, val: rate ?? 0, unit: '%', sub: rate === null ? `nothing ran in ${outcomeWin}` : outcomeWin, tip: 'Succeeded ÷ terminal runs (success + failed)' },
  ];

  const stopped = !!hb.data && hb.data.engineEnabled && !hb.data.pollerRunning;
  const beatS = hb.data?.secondsSinceLastCycle;
  const wedged = !!hb.data && hb.data.pollerRunning && beatS != null && beatS > Math.max(30, hb.data.pollIntervalSeconds * 3);

  return (
    <div className="ea-root" data-testid="BackgroundJobsDashboardView">
      <div className="ea-stack">
        {/* head + date filter */}
        <div className="ea-head">
          <div>
            <div className="ea-crumb"><b>Background Jobs Dashboard</b><span className="sep">/</span>{rangeLabel}</div>
            <h1>Background Jobs Dashboard</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Beside the date range, because the two answer the same question —
                what am I looking at — and the header already reserves this row
                for it. */}
            {canScopeByProject && (
              <Dropdown
                value={appFilter}
                options={projectOptions(projects, 'All projects')}
                optionLabel="label" optionValue="value" placeholder="All projects"
                showClear={!!appFilter} style={{ minWidth: 180 }}
                onChange={(e: { value: string }) => setAppFilter(e.value || '')}
                data-testid="BackgroundJobsDashboard.ProjectFilter"
              />
            )}
          <div className="ea-daterange">
            <button className={`ea-dr-trigger ${dateOpen ? 'open' : ''}`} onClick={() => setDateOpen((o) => !o)} aria-expanded={dateOpen} title={dateOpen ? 'Hide date range' : 'Change date range'}>
              {!dateOpen && (<>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" width="15"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /></svg>
                <span className="l">Date range</span><b>{rangeLabel}</b>
              </>)}
              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13"><path d="m9 6 6 6-6 6" /></svg>
            </button>
            {dateOpen && (
              <div className="ea-dr-pop">
                <div className="fields">
                  <div className="fld"><span className="lbl">Start date</span>
                    <Calendar value={startDate} onChange={(e: { value: Date | null }) => setStartDate(e.value ?? null)} showIcon showButtonBar dateFormat="dd M yy" placeholder="Any" maxDate={endDate ?? new Date()} appendTo={typeof document !== 'undefined' ? document.body : undefined} style={{ width: 150 }} />
                  </div>
                  <div className="fld"><span className="lbl">End date</span>
                    <Calendar value={endDate} onChange={(e: { value: Date | null }) => setEndDate(e.value ?? null)} showIcon showButtonBar dateFormat="dd M yy" placeholder="Any" minDate={startDate ?? undefined} maxDate={new Date()} appendTo={typeof document !== 'undefined' ? document.body : undefined} style={{ width: 150 }} />
                  </div>
                </div>
                <div className="presets"><span className="lbl">Quick select</span>
                  <div className="ea-seg">
                    {([['7d', 'Past 7 days', 7], ['30d', 'Past 30 days', 30], ['all', 'All time', null]] as const).map(([key, label, days]) => (
                      <button key={key} className={activePreset === key ? 'on' : ''} onClick={() => applyPreset(days)}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* engine health */}
        {hb.data && (
          <div className={`ea-enginebar ${stopped || wedged ? 'bad' : !hb.data.engineEnabled ? 'idle' : 'ok'}`}>
            <i className={`pi ${stopped || wedged ? 'pi-exclamation-triangle' : !hb.data.engineEnabled ? 'pi-pause' : 'pi-heart-fill'}`} />
            {!hb.data.engineEnabled ? <span><strong>Engine off.</strong> Schedules are stored but nothing runs.</span>
              : stopped ? <span><strong>Poller stopped.</strong> Enabled, but this instance is not polling — due jobs will not fire here.</span>
                : wedged ? <span><strong>Poller stuck.</strong> No cycle for {beatS}s, past the {hb.data.pollIntervalSeconds}s interval.</span>
                  : <span><strong>Engine healthy.</strong> Last beat {beatS == null ? 'no cycle yet' : beatS < 60 ? `${beatS}s ago` : `${Math.round(beatS / 60)}m ago`}, every {hb.data.pollIntervalSeconds}s.</span>}
            <span className="mini">this instance</span>
          </div>
        )}

        {/* KPI cards */}
        <div className="ea-kpis">
          {kpis.map((k, i) => (
            <div key={k.key} className={`ea-card ea-kpi ea-reveal ${k.drill ? 'clickable' : ''}`}
              style={{ animationDelay: `${i * 40}ms`, ['--kc' as string]: k.color, ['--kglow' as string]: `${k.color}99` } as CSSProperties}
              onClick={() => k.drill && setDrill(k.drill)}
              onMouseMove={(e) => showTip(<><div className="t">{k.label}</div><div className="r">{k.tip}</div></>, e.clientX, e.clientY)} onMouseLeave={hideTip}>
              <div className="top">
                <div className="ic" style={{ background: `linear-gradient(135deg, ${k.color}, ${k.color}bb)` }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="17"><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>
                </div>
                <div className="ea-kpi-meta">
                  <div className="lab">{k.label}</div>
                  <div className="val num">{k.text != null
                    ? <span style={{ fontSize: k.key === 'next' ? 20 : 18 }}>{k.text}</span>
                    : <CountUp value={k.val ?? 0} unit={k.unit} />}</div>
                </div>
              </div>
              {k.sub && <div className="ea-kpi-src"><span className="more" style={{ marginLeft: 'auto' }}>{k.sub}</span></div>}
              {k.drill && (
                <button className="ea-kpi-drill" onClick={(e) => { e.stopPropagation(); setDrill(k.drill!); }}>
                  View runs
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Overview / Records — the same split the Notification and Error Log
            dashboards use: the aggregate above, the rows behind it one click away,
            on the same screen rather than on another tab. Records renders the very
            same grid the Background Run tab does, so there is one implementation of
            run history and it cannot drift. */}
        <div className="ea-maintabs">
          <button className={view === 'overview' ? 'on' : ''} onClick={() => setView('overview')}
            data-testid="BackgroundJobDashboard.Tab.Overview">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>Overview
          </button>
          <button className={view === 'records' ? 'on' : ''} onClick={() => setView('records')}
            data-testid="BackgroundJobDashboard.Tab.Records">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></svg>Records
            {total > 0 && <span className="cnt num">{total.toLocaleString()}</span>}
          </button>
        </div>

        {view === 'overview' ? (
          /* live work */
          <section className="flex flex-col gap-3" style={{ marginTop: 8 }}>
            <div className="flex items-center gap-2 pt-1">
              <i className="pi pi-bolt text-gray-400" />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--ea-ink)' }}>Live work</h3>
              <span className="text-xs" style={{ color: 'var(--ea-ink-3)' }}>what is executing, and what the poller takes next</span>
            </div>
            <NowAndNext />
          </section>
        ) : (
          <section className="ea-card pad" style={{ marginTop: 8 }}>
            {canReadRuns
              ? <RunsTab />
              : (
                <div className="ea-modal-empty">
                  You do not have permission to read run history
                  (<code>BACKGROUND_JOB_RUN.READ</code>).
                </div>
              )}
          </section>
        )}
      </div>

      {/* tooltip */}
      {tip.show && <div className="ea-tt" style={{ left: Math.min(tip.x + 14, window.innerWidth - 250), top: Math.min(tip.y + 14, window.innerHeight - 150) }}>{tip.content}</div>}

      {/* drill dialog — runs of a status */}
      {drill && (
        <>
          <div className="ea-scrim show" onClick={() => setDrill(null)} />
          <div className="ea-modal" role="dialog" aria-modal="true">
            <div className="ea-modal-head">
              <div>
                <div className="ea-title" style={{ fontSize: 14 }}>{drill === 'ALL' ? 'All runs' : `${statusLabel[drill]} runs`}</div>
                <div className="ea-sub" style={{ marginTop: 3 }}>{fmt(drillRuns.length)} runs · {win}</div>
              </div>
              <button className="ea-iconbtn" onClick={() => setDrill(null)} aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
            </div>
            <div className="ea-modal-body">
              {drillRuns.slice(0, 200).map((r, i) => (
                <button aria-label="Open run" className="ea-modal-row" key={r._id + i} onClick={() => { setDetail(r); setDrill(null); }} title="Open run">
                  <span className="rk">{String(i + 1).padStart(2, '0')}</span>
                  <span className="body">
                    <span className="p">{jobName(r)}<StatusChip s={r.status} /></span>
                    <span className="s">attempt #{r.attemptNumber} · {trigLabel(r.triggeredBy)} · {timeAgo(startedOf(r))}</span>
                  </span>
                  <span className="ct num">{fmtDur(startedOf(r), r.endedAt)}</span>
                </button>
              ))}
              {!drillRuns.length && <div className="ea-modal-empty">No runs in the selected window.</div>}
            </div>
          </div>
        </>
      )}

      {/* run detail drawer */}
      <div className={`ea-scrim ${detail ? 'show' : ''}`} onClick={() => setDetail(null)} />
      <aside className={`ea-drawer ${detail ? 'show' : ''}`} aria-label="Run detail">
        {detail && (
          <>
            <div className="ea-dhead">
              <div className="ea-crumb" style={{ marginBottom: 12 }}>Run<span className="sep" style={{ margin: '0 7px' }}>›</span><b>{jobName(detail)}</b></div>
              <div className="top"><div><div className="ea-dcode">{jobName(detail)}</div><div className="ea-dttl">{detail.logSummary || `Attempt #${detail.attemptNumber}`}</div></div>
                <button className="ea-iconbtn" onClick={() => setDetail(null)} aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button></div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}><StatusChip s={detail.status} /><span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ea-ink-3)' }}>{trigLabel(detail.triggeredBy)}</span></div>
              <div className="ea-dstats">
                {([['Attempt', `#${detail.attemptNumber}`], ['Duration', fmtDur(startedOf(detail), detail.endedAt)], ['Started', timeAgo(startedOf(detail))], ['Trigger', trigLabel(detail.triggeredBy)]] as [string, string][]).map((s) => (
                  <div className="ea-dstat" key={s[0]}><div className="l">{s[0]}</div><div className="v num">{s[1]}</div></div>
                ))}
              </div>
            </div>
            <div className="ea-dbody">
              <JsonBlock label="Input parameters" value={detail.inputParameters} />
              <JsonBlock label="Resolved payload" value={detail.resolvedExecutionPayload} />
              <JsonBlock label="Output" value={detail.outputData} />
              {detail.logSummary && <div><div className="ea-dsec">Log summary</div><div style={{ fontSize: 12.5, color: 'var(--ea-ink-2)', lineHeight: 1.6 }}>{detail.logSummary}</div></div>}
              {detail.errorLogId && <div><div className="ea-dsec">Error log</div><div className="mono" style={{ fontSize: 12 }}>{detail.errorLogId}</div></div>}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const empty = !value || (typeof value === 'object' && Object.keys(value as object).length === 0);
  return (
    <div>
      <div className="ea-dsec">{label}</div>
      {empty ? <div style={{ fontSize: 12.5, color: 'var(--ea-ink-3)' }}>—</div>
        : <pre className="ea-strace" style={{ maxHeight: 220 }}>{JSON.stringify(value, null, 2)}</pre>}
    </div>
  );
}

export default DashboardView;
